import moment from "moment";
import pluralize from "pluralize";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import {
  ComplianceProgramRecurrence,
  CreateProgramScheduledReminderMutationMutationVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar, isAdminVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import CustomTextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";
import InfoTooltip from "@components/tooltip/info-tooltip";
import Tooltip from "@components/tooltip/tooltip";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxTeamOption,
} from "@components/user-combobox/user-combobox-list";
import { classNames, textareaClassName } from "@helpers/css";

import { useProgramsSection } from "../programs-section-context";
import {
  ComplianceProgramReportingEntityTableCol,
  ComplianceProgramReportingEntityTableKeyName,
} from "./compliance-program-progress-entity-table";

const ComplianceProgramReminderDialogRow = ({
  checked,
  col,
  onChangeCheckbox,
}: {
  checked: boolean;
  col: ComplianceProgramReportingEntityTableCol;
  onChangeCheckbox: (e: ChangeEvent<HTMLInputElement>) => void;
}) => {
  const users = col.remindUsers || [];
  const count = users.length;
  const subjectLabel = `${count} ${
    col.remindUserType === "survey_participant"
      ? pluralize("participant", count)
      : pluralize("subject", count)
  }`;
  return (
    <div className="flex flex-col gap-2 w-full py-2 px-2" key={col.keyName}>
      <div className="flex items-center gap-1 justify-between w-full">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={checked}
            name={col.keyName}
            onChange={onChangeCheckbox}
            className="cursor-pointer"
          />
          {col.label}
        </label>
        {col.remindUsers !== undefined && (
          <Tooltip text={col.remindUserTooltip}>
            <span className="text-gray-400 hover:text-gray-500 text-xs tracking-tighter flex items-center gap-1">
              {col.remindUserType === "manager" &&
                `Managers of ${subjectLabel}`}
              {(col.remindUserType === "subject" ||
                col.remindUserType === "survey_participant") &&
                subjectLabel}
              {col.remindUserType === "peer" &&
                `Peers nominated for ${subjectLabel}`}
              {col.remindUserType === "directReport" &&
                `Reports of ${subjectLabel}`}
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

const ComplianceProgramReminderDialog = ({
  complianceProgramStartDate,
  complianceProgramRecurrence,
  complianceProgramOngoing,
  showRelativeDateInput = false,
  cols,
  selectedTeam,
  createProgramReminderLoading,
  disallowSendNow = false,
  onClose,
  onSave,
}: {
  complianceProgramStartDate: Date;
  complianceProgramRecurrence: ComplianceProgramRecurrence;
  complianceProgramOngoing: boolean;
  createProgramReminderLoading: boolean;
  showRelativeDateInput?: boolean;
  cols: ComplianceProgramReportingEntityTableCol[];
  selectedTeam: UserComboboxTeamOption | null;
  disallowSendNow?: boolean;
  onClose: () => void;
  onSave: (
    variables: Omit<
      CreateProgramScheduledReminderMutationMutationVariables,
      "complianceProgramId"
    >,
  ) => void;
}) => {
  const [isScheduled, setIsScheduled] = useState(disallowSendNow);

  const recurringOrOngoing =
    complianceProgramOngoing ||
    complianceProgramRecurrence !== ComplianceProgramRecurrence.None;
  const needOneDayOffset =
    recurringOrOngoing || moment().isAfter(complianceProgramStartDate);

  const offsetDays = needOneDayOffset ? 1 : 0;
  const startDateDiff = moment().diff(complianceProgramStartDate, "days");
  const [daysAfterStartDate, setDaysAfterStartDate] = useState<string>(
    String(
      recurringOrOngoing || Number.isNaN(startDateDiff) || startDateDiff <= 0
        ? offsetDays
        : startDateDiff + 1,
    ),
  );

  const validatedDaysAfterStartDate = useMemo(() => {
    const value = parseInt(daysAfterStartDate);
    if (Number.isNaN(value) || value < offsetDays) {
      return String(offsetDays);
    }
    return String(value);
  }, [daysAfterStartDate, offsetDays]);

  const reminderDate = moment(complianceProgramStartDate)
    .add(validatedDaysAfterStartDate, "days")
    .toDate();

  // at a minimum tomorrow
  const minDatePicker = moment().isBefore(complianceProgramStartDate)
    ? complianceProgramStartDate
    : moment().add(1, "day").toDate();

  const isAdmin = isAdminVar();
  const currentOrganization = currentOrganizationVar();
  const label = useLabel();
  const { noun } = useProgramsSection();
  const [team, setTeam] = useState<UserComboboxTeamOption | null>(
    isAdmin ? selectedTeam : null,
  );
  const [message, setMessage] = useState("");
  const [emailNotification, setEmailNotification] = useState(
    !currentOrganization.hasSlackConnection &&
      !currentOrganization.hasTeamsNotificationConnection,
  );
  const [slackNotification, setSlackNotification] = useState(false);
  const [teamsNotification, setTeamsNotification] = useState(false);

  const initialCheckboxes = cols.reduce(
    (acc, col) => {
      acc[col.keyName] =
        col.keyName === "engagementSurveyStatus" ? true : false;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const [checkboxes, setCheckboxes] =
    useState<Record<string, boolean>>(initialCheckboxes);

  const { options, query, setQuery } = useUserComboboxQuery({
    types: [UserComboboxOptionType.TEAM],
    selected: team,
  });

  const handleChangeTeam = useCallback(
    (team: UserComboboxOption | null) => {
      if (team?.type === UserComboboxOptionType.TEAM) setTeam(team);
    },
    [setTeam],
  );

  const handleChangeCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setCheckboxes({ ...checkboxes, [e.target.name]: e.target.checked });
  };

  const dataToSave = cols.reduce(
    (acc, col) => {
      if (checkboxes[col.keyName]) {
        const userIds = col.remindUsers
          ? col.remindUsers
              .filter(
                (user) =>
                  !team || user.teamsList.some((team) => team?.id === team.id),
              )
              .map((user) => user.id)
          : [];
        acc[`${col.keyName}UserIds`] = userIds;
      }
      return acc;
    },
    {} as Record<
      `${ComplianceProgramReportingEntityTableKeyName}UserIds`,
      number[]
    >,
  );

  const handleClickSave = useCallback(() => {
    onSave({
      message,
      emailNotification: emailNotification,
      slackNotification: slackNotification,
      teamsNotification: teamsNotification,
      daysAfterStartDate: isScheduled ? Number(validatedDaysAfterStartDate) : 0,
      sendNow: !isScheduled,
      nominations: !!checkboxes.nominationsStatus,
      oneonone: !!checkboxes.oneononeStatus,
      performanceAssessment: !!checkboxes.performanceAssessmentStatus,
      performanceSelfAssessment: !!checkboxes.performanceSelfAssessmentStatus,
      managerAssessment: !!checkboxes.managerAssessmentStatus,
      peerAssessment: !!checkboxes.peerAssessmentStatus,
      engagementSurvey: !!checkboxes.engagementSurveyStatus,
    });
  }, [
    message,
    checkboxes,
    onSave,
    emailNotification,
    slackNotification,
    teamsNotification,
    validatedDaysAfterStartDate,
    isScheduled,
  ]);

  const handleChangeDaysAfterStartDate = (e: ChangeEvent<HTMLInputElement>) => {
    setDaysAfterStartDate(e.target.value);
  };

  const handleBlurDaysAfterStartDate = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    if (Number.isNaN(value) || value < 1 || String(value) !== e.target.value) {
      setDaysAfterStartDate(validatedDaysAfterStartDate);
    }
  };

  const handleChangeReminderDate = (date: Date | null) => {
    if (date) {
      const diff = moment(date).diff(complianceProgramStartDate, "days");
      setDaysAfterStartDate(String(Math.max(diff, 0)));
    }
  };

  const showDatePickerInput = !(
    showRelativeDateInput ||
    complianceProgramOngoing ||
    complianceProgramRecurrence !== ComplianceProgramRecurrence.None
  );

  const labelClassName = "text-gray-500 w-42 pt-1.5 shrink-0";
  return (
    <Modal onClose={onClose} className="" alignedTop>
      <div
        className="flex flex-col gap-6 p-6 text-sm max-h-[90vh] overflow-y-auto"
        aria-label="Program send email reminder dialog"
      >
        <ModalTitle onClose={onClose}>{noun} Reminder</ModalTitle>

        {!disallowSendNow && (
          <div className="flex gap-2">
            <label className={labelClassName}>Trigger Type</label>
            <div className="">
              <ToggleButtonGroup<"scheduled" | "sendnow">
                className="w-64"
                buttons={[
                  {
                    title: "Send now",
                    value: "sendnow",
                    active: !isScheduled,
                    className: "w-32 block text-center",
                  },
                  {
                    title: "Scheduled",
                    value: "scheduled",
                    active: isScheduled,
                    className: "w-32 block text-center",
                  },
                ]}
                theme={ToggleButtonGroupTheme.defaultButtons}
                onClick={(option) =>
                  setIsScheduled(option.value === "scheduled")
                }
              />
            </div>
          </div>
        )}
        {isScheduled && (
          <div className="flex gap-2">
            <label className={labelClassName}>Reminder Date</label>
            <div>
              {showDatePickerInput ? (
                <div className="flex items-center gap-2">
                  <InputDatePicker
                    className="w-64"
                    dateFormat="MMM d, yyyy"
                    date={reminderDate}
                    onChangeDate={handleChangeReminderDate}
                    isClearable={false}
                    minDate={minDatePicker}
                  />
                  at 9am
                  <InfoTooltip
                    text={`The reminders will be sent at 9am on the employee's local time.`}
                  />
                </div>
              ) : (
                <div className="flex items-center text-gray-500 gap-2">
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    className="w-18"
                    pattern="\d*"
                    value={daysAfterStartDate}
                    onChange={handleChangeDaysAfterStartDate}
                    onBlur={handleBlurDaysAfterStartDate}
                  />
                  days after the program start date at 9am
                  <InfoTooltip
                    text={`The reminders will be sent at 9am on the employee's local time.`}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* DISABLING FOR NOW */}
        {/* eslint-disable-next-line no-constant-binary-expression */}
        {false && (
          <div className="flex gap-2">
            <label className="text-gray-500 w-42 pt-1.5">
              Select {label("team", { titleCase: true })}
            </label>
            <div className="w-64">
              <UserCombobox
                value={team}
                onChangeValue={handleChangeTeam}
                query={query}
                onChangeQuery={setQuery}
                options={options}
                placeholder="Select..."
                clearable={team !== null}
                onClearValue={() => setTeam(null)}
              />
            </div>
          </div>
        )}

        {(currentOrganization.hasSlackConnection ||
          currentOrganization.hasTeamsNotificationConnection) && (
          <div className="flex gap-2">
            <label className="text-gray-500 w-42 pt-1.5">Reminder Type</label>
            <div className="flex-1 flex gap-6">
              <label className="flex items-center gap-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={emailNotification}
                  onChange={(e) => setEmailNotification(e.target.checked)}
                />
                Email
              </label>
              {currentOrganization.hasSlackConnection && (
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={slackNotification}
                    onChange={(e) => setSlackNotification(e.target.checked)}
                  />
                  Slack
                </label>
              )}
              {currentOrganization.hasTeamsNotificationConnection && (
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={teamsNotification}
                    onChange={(e) => setTeamsNotification(e.target.checked)}
                  />
                  Teams
                </label>
              )}
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <label className="text-gray-500 w-42 pt-1.5">Message</label>
          <div className="flex-1">
            <CustomTextareaAutosize
              className={classNames(textareaClassName, "min-h-20 max-h-36")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-gray-500 font-medium">
            Notify team members that need to take action on each of the steps
            below:
          </div>
          <div className="flex flex-col divide-y border-t border-b text-gray-600">
            {cols.map((col) => (
              <ComplianceProgramReminderDialogRow
                key={col.keyName}
                col={col}
                checked={checkboxes[col.keyName]}
                onChangeCheckbox={handleChangeCheckbox}
              />
            ))}
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button
            aria-label="Confirm reminder button"
            text={isScheduled ? "Schedule" : "Send Now"}
            theme={buttonTheme.primary}
            disabled={
              createProgramReminderLoading ||
              Object.keys(dataToSave).length === 0 ||
              (!emailNotification && !slackNotification && !teamsNotification)
            }
            tooltip={
              !emailNotification && !slackNotification && !teamsNotification
                ? "Please select at least one reminder type"
                : undefined
            }
            onClick={handleClickSave}
          />
          <Button text="Cancel" onClick={onClose} />
          {createProgramReminderLoading && <Loading mini size="5" />}
        </div>
      </div>
    </Modal>
  );
};
export default ComplianceProgramReminderDialog;
